import _ from "lodash";
import * as React from "react";

import LoaderGif from "../controls/LoaderGif";

import { DataService, IQuickPickStateResponse, IGamePickUpdateResponse } from "../../services/dataService";
import { popup, confirm } from "../../services/notifyService";
import { IAction } from "../../actions";
import { IGamePick, IAvailableTeamPick } from "../../models/interfaces";

export interface IQuickPickModalProps {
    authenticationToken: string;
    leagueId: number;
    leagueMemberId: number;
    leagueName: string;
    dispatch: (action: IAction) => void;
    onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const dialogStyle: React.CSSProperties = {
    backgroundColor: '#FFF',
    borderRadius: '4px',
    boxShadow: '0 3px 9px rgba(0, 0, 0, 0.5)',
    padding: '15px 20px 20px 20px',
    width: '90%',
    maxWidth: '400px'
};

const nbsp = '\u00a0';

const spreadText = (spread: number | null): string => {
    if (_.isUndefined(spread) || _.isNull(spread)) {
        return '';
    }
    if (spread > 0) {
        return `(+${spread.toString()})`;
    }
    if (spread < 0) {
        return `(${spread.toString()})`;
    }
    return '(±0)';
}

const futureWeekText = (pickedWeekNumber: number | null): string => {
    if (_.isUndefined(pickedWeekNumber) || _.isNull(pickedWeekNumber)) {
        return '';
    }
    return `[Week ${pickedWeekNumber.toString()}]`;
}

// options render as plain text, so the columns are aligned with a monospace
// font and non-breaking space padding (regular spaces collapse in <option>).
const optionLabel = (team: IAvailableTeamPick, nameWidth: number): string => {
    const name = team.teamName.padEnd(nameWidth + 2, nbsp);
    const spread = spreadText(team.spread).padStart(7, nbsp);
    const week = futureWeekText(team.pickedWeekNumber);
    return week.length > 0 ? `${name}${spread}${nbsp}${nbsp}${week}` : `${name}${spread}`;
}

const QuickPickModal = (props: IQuickPickModalProps) => {

    const [state, setState] = React.useState<IQuickPickStateResponse | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [selectedTeamId, setSelectedTeamId] = React.useState<number>(0);
    const [saving, setSaving] = React.useState<boolean>(false);

    React.useEffect(() => {
        DataService.getQuickPickState(
            props.authenticationToken,
            props.leagueId,
            props.leagueMemberId,
            (response: IQuickPickStateResponse) => {
                if (response.success) {
                    setState(response);
                    setSelectedTeamId(response.pickedTeamId);
                } else {
                    popup("Error", response.message, 'error');
                    props.onClose();
                }
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitPick = (withReplace: boolean): void => {
        if (!state) {
            return;
        }
        const team = _.find(state.availableTeamPicks, t => t.teamId === selectedTeamId);
        if (!team) {
            return;
        }
        setSaving(true);
        const gamePick = {
            gameId: team.gameId,
            leagueMemberPickId: state.leagueMemberPickId,
            origPickTeamId: state.pickedTeamId,
            currentPickTeamId: team.teamId,
            created: state.pickCreated != null ? state.pickCreated : new Date(),
            margin: 0
        } as unknown as IGamePick;

        DataService.updateGamePick(
            props.authenticationToken,
            props.leagueId,
            props.leagueMemberId,
            state.weekId,
            withReplace,
            gamePick,
            (response: IGamePickUpdateResponse) => {
                if (response.success) {
                    // refresh the leagues table (picks due and current pick)
                    DataService.getUserState(props.authenticationToken, false, props.dispatch);
                    props.onClose();
                } else if (response.overwritable) {
                    confirm("Replace Pick?", response.message)
                        .then((ok: Boolean) => {
                            if (ok) {
                                submitPick(true);
                            } else {
                                setSaving(false);
                            }
                        });
                } else {
                    popup("Error Making Pick", response.message, 'error');
                    setSaving(false);
                }
            },
            props.dispatch);
    }

    const okClicked = (): void => {
        if (!state) {
            return;
        }
        if (state.weekLocked || selectedTeamId === 0 || selectedTeamId === state.pickedTeamId) {
            // nothing to change.
            props.onClose();
            return;
        }
        submitPick(false);
    }

    const body = (): JSX.Element | null => {
        if (loading || !state) {
            return <p><LoaderGif gif="" visible={true} /> Loading...</p>;
        }
        if (state.weekLocked) {
            return (
                <span>
                    <p>This week is already locked.</p>
                    {state.pickedTeamName.length > 0
                        ? <p><b>{state.pickedTeamName}</b></p>
                        : <p>You did not make a pick this week.</p>}
                </span>
            );
        }
        const nameWidth = _.max(state.availableTeamPicks.map(t => t.teamName.length)) || 0;
        return (
            <span>
                <p>{props.leagueName}</p>
                <select
                    className="form-control"
                    style={{ fontFamily: 'Consolas, Menlo, Courier, monospace' }}
                    value={selectedTeamId}
                    onChange={e => setSelectedTeamId(parseInt(e.target.value, 10))}
                    >
                    {state.pickedTeamId === 0 && <option value={0}>&lt;select a team&gt;</option>}
                    {state.availableTeamPicks.map(t =>
                        <option key={t.teamId} value={t.teamId}>{optionLabel(t, nameWidth)}</option>)}
                </select>
            </span>
        );
    }

    const buttons = (): JSX.Element | null => {
        if (loading || !state) {
            return null;
        }
        if (state.weekLocked) {
            return (
                <button
                    type="submit"
                    className="btn btn-pickem-blue"
                    onClick={() => props.onClose()}
                    >OK</button>
            );
        }
        return (
            <span>
                <LoaderGif gif="" visible={saving} />
                &nbsp;
                <button
                    type="submit"
                    className="btn btn-pickem-blue button-margin"
                    disabled={saving || selectedTeamId === 0}
                    onClick={okClicked}
                    >OK</button>
                &nbsp;
                <button
                    type="submit"
                    className="btn btn-pickem-blue button-margin"
                    disabled={saving}
                    onClick={() => props.onClose()}
                    >Cancel</button>
            </span>
        );
    }

    return (
        <div style={overlayStyle}>
            <div style={dialogStyle}>
                <h4>Select Week {state ? state.weekNumber : ''} Pick</h4>
                {body()}
                <div style={{ marginTop: '15px', textAlign: 'right' }}>
                    {buttons()}
                </div>
            </div>
        </div>
    );
}

export default QuickPickModal;
